import { API_BASE_URL } from "./config";

// Web Push requires the VAPID public key as a Uint8Array, but browsers hand
// it back/forward as base64url — this is the standard conversion.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);
}

// Read-only check, doesn't request permission — only call this from a user
// gesture (e.g. opening the notification dropdown), never unconditionally on
// mount: some browser/OS combinations (seen on iOS standalone home-screen
// installs) can leave navigator.serviceWorker.getRegistration() hanging
// indefinitely, and this must never be able to stall the rest of the app.
export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  try {
    const registration = await withTimeout(navigator.serviceWorker.getRegistration("/sw.js"), 3000);
    if (!registration) return null;
    return await withTimeout(registration.pushManager.getSubscription(), 3000);
  } catch (e) {
    return null;
  }
}

export async function subscribeToPush(accessToken) {
  if (!isPushSupported()) {
    throw new Error("Browser ini tidak mendukung push notification.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Izin notifikasi ditolak.");
  }

  const registration = await navigator.serviceWorker.register("/sw.js");

  const keyRes = await fetch(`${API_BASE_URL}/api/push/vapid-public-key/`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!keyRes.ok) {
    throw new Error("Gagal mengambil VAPID public key dari server.");
  }
  const { public_key: publicKey } = await keyRes.json();
  if (!publicKey) {
    throw new Error("Server belum dikonfigurasi untuk push notification (VAPID key kosong).");
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (err) {
      // Brave disables Google's push service (FCM) by default for privacy —
      // subscribe() fails with exactly this message, on every Brave install,
      // until the user flips it on manually. Chrome/Edge/Firefox never hit this.
      if (/push service error/i.test(err.message || "")) {
        throw new Error(
          "Push gagal didaftarkan. Kalau pakai Brave: buka brave://settings/privacy, " +
          "nyalain \"Use Google services for push messaging\", lalu coba lagi."
        );
      }
      throw err;
    }
  }

  const subJson = subscription.toJSON();
  const res = await fetch(`${API_BASE_URL}/api/push/subscribe/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(subJson),
  });
  if (!res.ok) {
    throw new Error("Gagal mendaftarkan subscription ke server.");
  }

  return subscription;
}

export async function unsubscribeFromPush(accessToken) {
  if (!isPushSupported()) return;

  const registration = await navigator.serviceWorker.getRegistration("/sw.js");
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  await fetch(`${API_BASE_URL}/api/push/unsubscribe/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });

  await subscription.unsubscribe();
}
